import {createSlice} from '@reduxjs/toolkit';
import {getAllNamespacesSuccess} from 'src/openapi/admin/apis/EntitlementNamespacesApi';
import {getAccountManagerTypesSuccess} from 'src/openapi/admin/apis/GetAccountManagerTypesApi';
import {getAccountStatusesSuccess} from 'src/openapi/admin/apis/GetAccountStatusesApi';
import {getAvailableAccessRightsSuccess} from 'src/openapi/admin/apis/GetAvailableAccessRightsApi';
import {getAvailableCanadianProvincesSuccess} from 'src/openapi/admin/apis/GetAvailableCanadianProvincesApi';
import {getAvailableUsStatesSuccess} from 'src/openapi/admin/apis/GetAvailableUsStatesApi';
import {getBusinessLinesSuccess} from 'src/openapi/admin/apis/GetBusinessLinesApi';
import {getCommonLimitsSuccess} from 'src/openapi/admin/apis/GetCommonLimitsApi';
import {getCountryCodesSuccess} from 'src/openapi/admin/apis/GetCountryCodesApi';
import {getDatesFormatsSuccess} from 'src/openapi/admin/apis/GetDatesFormatsApi';
import {getDecimalsFormatsSuccess} from 'src/openapi/admin/apis/GetDecimalsFormatsApi';
import {getEntityTypesSuccess} from 'src/openapi/admin/apis/GetEntityTypesApi';
import {getInitiatorSystemsFailure, getInitiatorSystemsSuccess} from 'src/openapi/admin/apis/GetInitiatorSystemsApi';
import {getLicenseCrmModulesSuccess} from 'src/openapi/admin/apis/GetLicenseCrmModulesApi';
import {getLicenseDataModulesSuccess} from 'src/openapi/admin/apis/GetLicenseDataModulesApi';
import {getLicenseFeatureModulesSuccess} from 'src/openapi/admin/apis/GetLicenseFeatureModulesApi';
import {getLicenseFeaturesSuccess} from 'src/openapi/admin/apis/GetLicenseFeaturesApi';
import {getLicenseTypesSuccess} from 'src/openapi/admin/apis/GetLicenseTypesApi';
import {getPapersFormatsSuccess} from 'src/openapi/admin/apis/GetPapersFormatsApi';
import {getSearchLimitsSuccess} from 'src/openapi/admin/apis/GetSearchLimitsApi';
import {getSupportedCurrenciesSuccess} from 'src/openapi/admin/apis/GetSupportedCurrenciesApi';
import {getSymbolsPositionsSuccess} from 'src/openapi/admin/apis/GetSymbolsPositionsApi';
import {getThousandsFormatsSuccess} from 'src/openapi/admin/apis/GetThousandsFormatsApi';
import {getUserStatusesSuccess} from 'src/openapi/admin/apis/GetUserStatusesApi';
import {getUserTypesSuccess} from 'src/openapi/admin/apis/GetUserTypesApi';
import {getLcdSubscriptionsTreesSuccess} from 'src/openapi/admin/apis/LcdSubscriptionsDictionaryApi';
import {getSupportedNewsletterTypesSuccess} from 'src/openapi/admin/apis/NewsletterDictionaryApi';
import {getAvailableProductsFailure, getAvailableProductsSuccess} from 'src/openapi/admin/apis/ProductsApi';
import {getAlertFrequencyTypesSuccess, getLayoutTypesSuccess, getSavedListTypesSuccess, getSavedSearchTypesSuccess} from 'src/openapi/admin/apis/UserContentDictionariesApi';

import type {PayloadAction} from '@reduxjs/toolkit';
import type {CommonLimitDictionaryItem} from 'src/openapi/admin/models/CommonLimitDictionaryItem';
import type {Currency} from 'src/openapi/admin/models/Currency';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {ProductSummary} from 'src/openapi/admin/models/ProductSummary';
import type {AppState} from 'src/store/typedef';

type InitialState = {
    products: ProductSummary[],
    productsStatus: 'idle' | 'loading' | 'succeeded' | 'failed',
    productsError: ApiError | null,
    initiatorSystems: DictionaryItem[],
    initiatorSystemsStatus: 'idle' | 'loading' | 'succeeded' | 'failed',
    accountStatuses: Array<DictionaryItem>,
    licenseTypes: Array<DictionaryItem>,
    accountEntityTypes: Array<DictionaryItem>,
    newsLetterTypes: Array<DictionaryTree>,
    lcdSubscriptionTrees: Array<DictionaryTree>,
    countries: Array<DictionaryItem>,
    accountManagerTypes: Array<DictionaryItem>,
    userTypes: Array<DictionaryItem>,
    userStatuses: Array<DictionaryItem>,
    licenseDataModules: Array<DictionaryTree>,
    licenseFeatureModules: Array<DictionaryItem>,
    licenseFeatures: Array<DictionaryItem>,
    licenseCommonLimits: Array<CommonLimitDictionaryItem>,
    searchLimits: Array<DictionaryItem>,
    businessLines: Array<DictionaryItem>,
    dateFormat: Array<DictionaryItem>,
    decimalFormats: Array<DictionaryItem>,
    paperFormats: Array<DictionaryItem>,
    currencies: Array<Currency>,
    symbolPositions: Array<DictionaryItem>,
    thousandFormats: Array<DictionaryItem>,
    availableAdminRights: Array<DictionaryItem>,
    usStates: Array<DictionaryItem>,
    canadianProvinces: Array<DictionaryItem>,

    licenseCrmModules: DictionaryItem[],
    entitlementNamespaces: DictionaryItem[],

    alertFrequencyTypes: Array<DictionaryItem>,
    savedSearchTypes: Array<DictionaryItem>,
    savedListTypes: Array<DictionaryItem>,
    layoutTypes: Array<DictionaryItem>,

    myAccounts: number[],
};

export type DictionarySelector = (state: AppState) => Array<DictionaryItem>;
export type CurrencySelector = (state: AppState) => Array<Currency>;

export const initialState: InitialState = {
    products: [],
    productsStatus: 'idle',
    productsError: null,
    initiatorSystems: [],
    initiatorSystemsStatus: 'idle',
    accountStatuses: [],
    licenseTypes: [],
    accountEntityTypes: [],
    newsLetterTypes: [],
    lcdSubscriptionTrees: [],
    countries: [],
    accountManagerTypes: [],
    userTypes: [],
    userStatuses: [],
    licenseDataModules: [],
    licenseFeatureModules: [],
    licenseFeatures: [],
    licenseCommonLimits: [],
    searchLimits: [],
    businessLines: [],
    dateFormat: [],
    decimalFormats: [],
    paperFormats: [],
    currencies: [],
    symbolPositions: [],
    thousandFormats: [],
    availableAdminRights: [],
    usStates: [],
    canadianProvinces: [],

    licenseCrmModules: [],
    entitlementNamespaces: [],

    alertFrequencyTypes: [],
    savedSearchTypes: [],
    savedListTypes: [],
    layoutTypes: [],

    myAccounts: [],
};

export const dictionarySlice = createSlice({
    name: 'dictionaries',
    initialState,
    reducers: {
        productsLoading(state) {
            state.productsStatus = 'loading';
            state.productsError = null;
        },
        productsFailed(state, action: PayloadAction<ApiError>) {
            state.productsStatus = 'failed';
            state.productsError = action.payload;
        },
        initiatorSystemsLoading(state) {
            state.initiatorSystemsStatus = 'loading';
        },
        initiatorSystemsFailed(state) {
            state.initiatorSystemsStatus = 'failed';
        },
        resetNewsLetterTypes(state) {
            state.newsLetterTypes = [];
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getAvailableProductsSuccess, (state, action) => {
                state.products = action.payload.data;
                state.productsStatus = 'succeeded';
                state.productsError = null;
            })
            .addCase(getAvailableProductsFailure, (state, action) => {
                state.productsStatus = 'failed';
                state.productsError = action.payload.data;
            })
            .addCase(getInitiatorSystemsSuccess, (state, action) => {
                state.initiatorSystems = action.payload.data;
                state.initiatorSystemsStatus = 'succeeded';
            })
            .addCase(getInitiatorSystemsFailure, state => {
                state.initiatorSystemsStatus = 'failed';
            })
            .addCase(getAccountStatusesSuccess, (state, action) => {
                state.accountStatuses = action.payload.data;
            })
            .addCase(getLicenseTypesSuccess, (state, action) => {
                state.licenseTypes = action.payload.data;
            })
            .addCase(getEntityTypesSuccess, (state, action) => {
                state.accountEntityTypes = action.payload.data;
            })
            .addCase(getCountryCodesSuccess, (state, action) => {
                state.countries = action.payload.data;
            })
            .addCase(getAccountManagerTypesSuccess, (state, action) => {
                state.accountManagerTypes = [{code: '', description: 'None'}, ...action.payload.data];
            })
            .addCase(getUserTypesSuccess, (state, action) => {
                state.userTypes = action.payload.data;
            })
            .addCase(getUserStatusesSuccess, (state, action) => {
                state.userStatuses = action.payload.data;
            })
            .addCase(getLicenseDataModulesSuccess, (state, action) => {
                state.licenseDataModules = action.payload.data;
            })
            .addCase(getLicenseFeaturesSuccess, (state, action) => {
                state.licenseFeatures = action.payload.data;
            })
            .addCase(getLicenseFeatureModulesSuccess, (state, action) => {
                state.licenseFeatureModules = action.payload.data;
            })
            .addCase(getCommonLimitsSuccess, (state, action) => {
                state.licenseCommonLimits = action.payload.data;
            })
            .addCase(getSearchLimitsSuccess, (state, action) => {
                state.searchLimits = action.payload.data;
            })
            .addCase(getBusinessLinesSuccess, (state, action) => {
                state.businessLines = action.payload.data;
            })
            .addCase(getDatesFormatsSuccess, (state, action) => {
                state.dateFormat = action.payload.data;
            })
            .addCase(getDecimalsFormatsSuccess, (state, action) => {
                state.decimalFormats = action.payload.data;
            })
            .addCase(getPapersFormatsSuccess, (state, action) => {
                state.paperFormats = action.payload.data;
            })
            .addCase(getSupportedCurrenciesSuccess, (state, action) => {
                state.currencies = action.payload.data;
            })
            .addCase(getSymbolsPositionsSuccess, (state, action) => {
                state.symbolPositions = action.payload.data;
            })
            .addCase(getThousandsFormatsSuccess, (state, action) => {
                state.thousandFormats = action.payload.data;
            })
            .addCase(getAvailableUsStatesSuccess, (state, action) => {
                state.usStates = action.payload.data;
            })
            .addCase(getAvailableCanadianProvincesSuccess, (state, action) => {
                state.canadianProvinces = action.payload.data;
            })
            .addCase(getAvailableAccessRightsSuccess, (state, action) => {
                state.availableAdminRights = action.payload.data;
            })
            .addCase(getLicenseCrmModulesSuccess, (state, action) => {
                state.licenseCrmModules = action.payload.data;
            })
            .addCase(getAllNamespacesSuccess, (state, action) => {
                state.entitlementNamespaces = action.payload.data.map(namespace => ({
                    code: namespace.code,
                    description: namespace.name,
                }));
            })
            .addCase(getAlertFrequencyTypesSuccess, (state, action) => {
                state.alertFrequencyTypes = action.payload.data;
            })
            .addCase(getSavedSearchTypesSuccess, (state, action) => {
                state.savedSearchTypes = action.payload.data;
            })
            .addCase(getSavedListTypesSuccess, (state, action) => {
                state.savedListTypes = action.payload.data;
            })
            .addCase(getLayoutTypesSuccess, (state, action) => {
                state.layoutTypes = action.payload.data;
            })
            .addCase(getSupportedNewsletterTypesSuccess, (state, action) => {
                state.newsLetterTypes = action.payload.data;
            })
            .addCase(getLcdSubscriptionsTreesSuccess, (state, action) => {
                state.lcdSubscriptionTrees = action.payload.data;
            });
    },
});

export const {productsLoading, productsFailed} = dictionarySlice.actions;

export const selectProducts = (state: AppState) => state.dictionaries.products;
export const selectProductsStatus = (state: AppState) => state.dictionaries.productsStatus;
export const selectProductsError = (state: AppState) => state.dictionaries.productsError;

export const {resetNewsLetterTypes, initiatorSystemsLoading, initiatorSystemsFailed} = dictionarySlice.actions;

export const selectInitiatorSystems = (state: AppState) => state.dictionaries.initiatorSystems;
export const selectInitiatorSystemsStatus = (state: AppState) => state.dictionaries.initiatorSystemsStatus;

export const selectAccountStatuses: DictionarySelector = state =>
    state.dictionaries.accountStatuses;

export const selectLicenseTypes: DictionarySelector = state =>
    state.dictionaries.licenseTypes;

export const selectAccountEntityTypes: DictionarySelector = state =>
    state.dictionaries.accountEntityTypes;

export const selectNewsLetterTypes: DictionaryLicenseTree = state =>
    state.dictionaries.newsLetterTypes;

export const selectLcdSubscriptionTrees: DictionaryLicenseTree = state =>
    state.dictionaries.lcdSubscriptionTrees;

export const selectCountries: DictionarySelector = state =>
    state.dictionaries.countries;

export const selectAccountManagerTypes: DictionarySelector = state =>
    state.dictionaries.accountManagerTypes;

export const selectUserTypes: DictionarySelector = state =>
    state.dictionaries.userTypes;

export const selectUserStatuses: DictionarySelector = state =>
    state.dictionaries.userStatuses;

type DictionaryLicenseTree = (state: AppState) => Array<DictionaryTree>;
export const selectLicenseDataModules: DictionaryLicenseTree = state =>
    state.dictionaries.licenseDataModules;

export const selectLicenseFeatureModules: DictionarySelector = state =>
    state.dictionaries.licenseFeatureModules;

export const selectLicenseFeatures: DictionarySelector = state =>
    state.dictionaries.licenseFeatures;

export const selectLicenseCommonLimits = (state: AppState) => state.dictionaries.licenseCommonLimits;

export const selectSearchLimits: DictionarySelector = state => state.dictionaries.searchLimits;

export const selectBusinessLines: DictionarySelector = state => state.dictionaries.businessLines;

export const selectDateFormats: DictionarySelector = state => state.dictionaries.dateFormat;

export const selectDecimalFormats: DictionarySelector = state => state.dictionaries.decimalFormats;

export const selectPaperFormats: DictionarySelector = state => state.dictionaries.paperFormats;

export const selectCurrencies: CurrencySelector = state => state.dictionaries.currencies;

export const selectSymbolPositions: DictionarySelector = state => state.dictionaries.symbolPositions;

export const selectThousandFormats: DictionarySelector = state => state.dictionaries.thousandFormats;

export const selectAvailableAdminRights: DictionarySelector = state =>
    state.dictionaries.availableAdminRights;
export const selectUsStates: DictionarySelector = state => state.dictionaries.usStates;
export const selectCanadianProvinces: DictionarySelector = state => state.dictionaries.canadianProvinces;

export const selectLicenseCrmModules: (state: AppState) => Array<DictionaryItem> = state =>
    state.dictionaries.licenseCrmModules;

export const selectEntitlementNamespaces: (state: AppState) => Array<DictionaryItem> = state =>
    state.dictionaries.entitlementNamespaces;

export const selectAlertFrequencyTypes: DictionarySelector = state =>
    state.dictionaries.alertFrequencyTypes;

export const selectSavedSearchTypes: DictionarySelector = state =>
    state.dictionaries.savedSearchTypes;

export const selectSavedListTypes: DictionarySelector = state =>
    state.dictionaries.savedListTypes;

export const selectLayoutTypes: DictionarySelector = state =>
    state.dictionaries.layoutTypes;
